<template>
    <div class="sidebar-form-configuration">
        <SidebarPanelHeader title="Control Configuration"
                            @save="save(false)"
                            @saveAndClose="save(true)"
                            @close="requestClose" />

        <!-- Basic of the control/Same for all -->
        <ControlBasicInformation
            v-if="permissions.canUpdateControlBasicDetail"
            :control="control"
            :siteID="siteID"
            :enabledLanguages="enabledTranslationLanguages"
            :formType="formType"
        />

        <!-- File-upload controls only: this field's files go to its own Open Dental Imaging category
             (blank = the form's uploads category). The backend reads control.attachmentCategory from the saved form. -->
        <div
            v-if="isUploadControl && permissions.canUpdateControlBasicDetail"
            class="form-group control-upload-category"
        >
            <label>Save uploaded images / files with this form to Open Dental Imaging module under Category:</label>
            <select class="form-control" v-model="control.attachmentCategory">
                <option value="">Use the form's setting ({{ formUploadsCategoryName }})</option>
                <option v-for="item in uploadCategoryOptions"
                        :key="item.DefNum"
                        :value="String(item.DefNum)">
                    {{ item.ItemName }}
                </option>
            </select>
            <small class="upload-category-hint">Overrides the form's uploads category for files uploaded in this field only. Leave it on the form's setting to keep all uploads together.</small>
        </div>

        <ControlStylingInformation
            v-if="permissions.canUpdateControlStyling"
            :control="control"
        />

        <!-- PDF/Submission alert highlight toggle -->
        <div
            v-if="permissions.canUpdateControlStyling"
            class="form-group checkbox-group control-alert-highlight"
        >
            <label>
                <input type="checkbox" v-model="control.highlightForAlert">
                Highlight for Alert
            </label>
            <small class="alert-highlight-hint">
                If you check this off, this specific control/area will be highlighted in red on the generated PDF and submission view to catch the reviewer's attention, so it's not missed.
            </small>
            <small
                v-if="control.highlightForAlert && isChoiceControl"
                class="alert-highlight-choice-hint"
            >
                <template v-if="controlType === 'opendentaldata'">
                    For this Open Dental Data Field, expand <strong>Control Specific Configuration</strong> below and pick the options that should trigger the highlight. If you pick none, any selection other than None triggers it; None on its own never does.
                </template>
                <template v-else>
                    For this {{ controlType }} question, expand <strong>Control Specific Configuration</strong> below and use <strong>Flag as alert</strong> next to each option to choose which specific answers should trigger the highlight. If no options are flagged, any non-empty answer will trigger the highlight.
                </template>
            </small>
        </div>

        <!-- Control specific configuration / Only render it if the control has specific configuration view -->
        <SidebarToggleableContainer
                v-if="specificConfigurationView && permissions.canUpdateControlSpecialConfiguration"
                headline="Control Specific Configuration"
                :siteID="siteID"
        >
            <component
                :is="specificConfigurationView"
                :formData="formData"
                :control="control"
                :siteID="siteID"
            />
        </SidebarToggleableContainer>

        <!-- Validation of the control / same for all -->
        <ControlValidationInformation
            v-if="!isValidationDisabled && permissions.canUpdateControlValidation"
            :control="control"
            :allowed-rules="allowedValidationRules"
            :rule-descriptions="validationRuleDescriptions"
        />

        <SidebarToggleableContainer
                v-if="permissions.canUpdateControlSpecialConfiguration"
                headline="Visibility / Conditional Logic"
                :siteID="siteID"
        >
            <VisibilityRulesEditor :control="control" :formData="formData" :availableFields="availableFields" @update-rules="onUpdateRules" />
            <!-- Debug panel removed -->
        </SidebarToggleableContainer>

        <div class="buttons">
            <button :class="styles.BUTTON.PRIMARY" @click="save(false)">
                Save
            </button>
            <button :class="styles.BUTTON.INFO" @click="save(true)">
                Save & Close
            </button>
        </div>
    </div>
</template>

<script>
    import {STYLE_INJECTION_MIXIN} from "../../../mixins/style-injection-mixin";
    import {SIDEBAR_BODY_MIXIN} from "../../../mixins/sidebar-body-mixin";
    import { langSuffix, LANGUAGE_CONFIG } from "../../../utils/languageHelper";
    import SidebarToggleableContainer from "../../../views/container-views/SidebarToggleableContainer";
    import ControlBasicInformation from "../control-configuration-views/ControlBasicInformation";
    import {CONTROLS} from "../../../configs/controls";
    import ControlStylingInformation from "../control-configuration-views/ControlStylingInformation";
    import {HELPER} from "../../../libraries/helper";
    import {FILE_UPLOAD_TYPES} from "../../../libraries/collect-attachments";
    import ControlValidationInformation from "../control-configuration-views/ControlValidationInformation";
    import VisibilityRulesEditor from "../control-configuration-views/ControlVisibilityRules";
    import {getOdReferralMappingError} from "../../../libraries/validate-od-referral-mapping";
    import {getOdCategorizedMappingError, getOdCategorizedOtherProblemsError} from "../../../libraries/od-categorized-diseases";
    import {getOdUniqueMappingError} from "../../../libraries/validate-od-unique-mapping";
    import SidebarPanelHeader from "./SidebarPanelHeader";

    export default {
        name: "SidebarControlConfiguration",
        components: {
            SidebarPanelHeader,
            ControlValidationInformation,
            ControlStylingInformation,
            ControlBasicInformation,
            SidebarToggleableContainer,
            VisibilityRulesEditor
        },
        mixins: [STYLE_INJECTION_MIXIN, SIDEBAR_BODY_MIXIN],

        props: {
            permissions: Object,
            siteID: String,
            formType: {
                type: String,
                default: null
            }
        },

        data:() => ({
            dataKey: "control",
            control: null,
            lastLabel: null
        }),

        created() {
            // clone to make sure, no references will interrupt the configuration...
            this.control = HELPER.cloneDeep(this.dataPackage)
            // Vue 2 reactivity: the key was never part of any control default, so without this
            // the alert card's dependent bindings (the hint, the OD alertOptions picker) don't
            // react on the first tick. The predicate checks === true, so false is harmless.
            if (this.control && !this.control.hasOwnProperty('highlightForAlert')) {
                this.$set(this.control, 'highlightForAlert', false)
            }
            this.lastLabel = this.control && this.control.label
        },
        watch: {
            // Watch for label changes and sync to formData.controls
            'control.label': function(newLabel) {
                if (!this.formData || !this.control || !this.control.uniqueId) return
                const ctrlId = this.control.uniqueId || this.control.uniqueid
                if (this.formData.controls && this.formData.controls[ctrlId]) {
                    this.formData.controls[ctrlId].label = newLabel
                }
            }
        },

        computed: {
            /**
             * Enabled non-English translation languages for this form
             */
            enabledTranslationLanguages() {
                if (!this.formData || !this.formData.formConfig) return [];
                const supported = this.formData.formConfig.supportedLanguages || [];
                return supported
                    .filter(code => code !== 'en' && LANGUAGE_CONFIG[code])
                    .map(code => ({ code, name: LANGUAGE_CONFIG[code].name, suffix: langSuffix(code) }));
            },

            /**
             * Quick'n'Short access to the control type
             */
            controlType() {
                return this.control.type
            },

            /**
             * True if this control has a per-option alert picker (radio/checkbox/dropDown carry
             * per-item "Flag as alert"; opendentaldata carries the alertOptions multiselect).
             * Drives the secondary hint in the alert card.
             */
            isChoiceControl() {
                return ['radio', 'checkbox', 'dropDown', 'opendentaldata'].includes(this.controlType)
            },

            /**
             * True for the file-upload controls ('fileUploader' and legacy 'attachment'). The list is the
             * one the submit path uses (collectFormAttachments), so the two can never disagree.
             */
            isUploadControl() {
                return !!this.control && FILE_UPLOAD_TYPES.includes(this.control.type)
            },

            /** The form's Open Dental Imaging categories ([{DefNum, ItemName}], attached by the form loader). */
            uploadCategoryOptions() {
                const cfg = this.formData && this.formData.formConfig
                return cfg && Array.isArray(cfg.pdfCategories) ? cfg.pdfCategories : []
            },

            /** ItemName of the form-level uploads category (what a blank override falls back to), else "not set". */
            formUploadsCategoryName() {
                const cfg = this.formData && this.formData.formConfig
                const picked = cfg && cfg.attachmentCategory != null ? String(cfg.attachmentCategory).trim() : ''
                if (picked === '') return 'not set'
                const match = this.uploadCategoryOptions.find(item => String(item.DefNum) === picked)
                return match ? match.ItemName : 'not set'
            },

            /**
             * CHeck if the control doesn't need validation
             */
            isValidationDisabled() {
                if (CONTROLS[this.controlType].disableValidation) {
                    return true
                }

                return false
            },

            /** Per-control allowlist of validation rule keys (null = every rule in VALIDATION_RULES). */
            allowedValidationRules() {
                const def = CONTROLS[this.controlType] || {}
                return Array.isArray(def.allowedValidationRules) && def.allowedValidationRules.length
                    ? def.allowedValidationRules
                    : null
            },

            /** Per-control override of the rule hint text shown under the rule dropdown. */
            validationRuleDescriptions() {
                const def = CONTROLS[this.controlType] || {}
                return def.validationRuleDescriptions && typeof def.validationRuleDescriptions === 'object'
                    ? def.validationRuleDescriptions
                    : null
            },

            /**
             * Pick-up the specific configuration View for the Control
             * Depend on the `configComponent` of CONTROLS in `src/configs/controls.js`
             * If there's none => No Specific Configuration for the field
             * @returns VueComponent
             */
            specificConfigurationView() {
                if (!CONTROLS[this.controlType].hasOwnProperty('configComponent')) {
                    return null
                }

                // NOTE: this is a hash map access , not 2d array =))
                return CONTROLS[this.controlType]['configComponent'];
            }
        ,
        availableFields() {
            // Build a list of all fields grouped by section, excluding the current control
            const fd = this.formData || {}

            // Get current control identifier to exclude it
            const currentCtrl = this.control || {}
            const currentCtrlId = currentCtrl.uniqueId || currentCtrl.uniqueid
            const currentCtrlName = currentCtrl.name || currentCtrlId

            // Helper: normalized section id
            const normSectionId = (s, idx) => s && (s.uniqueId || s.uniqueid || s.id) || `section_${idx}`

            // Prepare section map from fd.sections
            const sectionMap = {}
            const sectionOrder = []
            if (fd.sections) {
                const sectionsArr = Array.isArray(fd.sections) ? fd.sections : Object.values(fd.sections)
                sectionsArr.forEach((s, i) => {
                    const sid = normSectionId(s, i)
                    const raw = s && (s.headline || s.name) || sid || `Section ${i+1}`
                    const label = s && (s.uniqueId || s.uniqueid) ? `${raw} (${s.uniqueId || s.uniqueid})` : raw
                    sectionMap[sid] = { sectionName: label, fields: [] }
                    sectionOrder.push(sid)
                })
            }

            // Iterate through sections and get controls directly from section.controls array
            if (fd.sections) {
                const sectionsArr = Array.isArray(fd.sections) ? fd.sections : Object.values(fd.sections)
                sectionsArr.forEach((s, si) => {
                    const sid = normSectionId(s, si)

                    // Controls are stored directly in section.controls as array of control IDs
                    const controlIds = Array.isArray(s.controls) ? s.controls : []
                    controlIds.forEach(controlId => {
                        // Skip the current control
                        if (controlId === currentCtrlId) return

                        // Look up the control in fd.controls
                        const ctrl = fd.controls && fd.controls[controlId]
                        if (!ctrl) return

                        // Use control.name if available, otherwise fall back to uniqueId
                        const name = ctrl.name || ctrl.uniqueId || ctrl.uniqueid || controlId

                        // Skip if this is the current control (by name)
                        if (name === currentCtrlName) return

                        if (!sectionMap[sid]) sectionMap[sid] = { sectionName: sid, fields: [] }
                        if (!sectionMap[sid].fields.find(f => f.name === name)) {
                            sectionMap[sid].fields.push({ name, label: ctrl.label || ctrl.name || name, type: ctrl.type })
                        }
                    })

                    // Also check rows if they exist (legacy/alternate structure)
                    const rowIds = Array.isArray(s.rows) ? s.rows : []
                    rowIds.forEach(rowId => {
                        const row = fd.rows && fd.rows[rowId]
                        if (!row) return
                        const rowControls = Array.isArray(row.controls) ? row.controls : []
                        rowControls.forEach(cref => {
                            const ctrlId = typeof cref === 'string' ? cref : (cref && (cref.uniqueId || cref.uniqueid))
                            // Skip the current control
                            if (ctrlId === currentCtrlId) return

                            const ctrl = typeof cref === 'string' ? (fd.controls && fd.controls[cref]) : cref
                            if (!ctrl) return
                            const name = ctrl.name || ctrl.uniqueId || ctrl.uniqueid
                            if (!name || name === currentCtrlName) return

                            if (!sectionMap[sid]) sectionMap[sid] = { sectionName: sid, fields: [] }
                            if (!sectionMap[sid].fields.find(f => f.name === name)) {
                                sectionMap[sid].fields.push({ name, label: ctrl.label || ctrl.name || name, type: ctrl.type })
                            }
                        })
                    })
                })
            }

            // Build result array preserving section order
            const result = []
            sectionOrder.forEach(sid => {
                if (sectionMap[sid] && sectionMap[sid].fields.length > 0) {
                    result.push(sectionMap[sid])
                }
            })

            return result
        }
        },
        methods: {
            /**
             * Called by SIDEBAR_BODY_MIXIN.requestClose() once "Discard your unsaved changes?" is confirmed.
             * The `control.label` watcher mirrors every keystroke into formData.controls (live preview on
             * the canvas), so the label as of the last Save (or the open) goes back there.
             */
            discardChanges() {
                const clean = this.cleanData()
                const ctrlId = this.control && (this.control.uniqueId || this.control.uniqueid)
                if (clean && ctrlId && this.formData && this.formData.controls && this.formData.controls[ctrlId]) {
                    this.formData.controls[ctrlId].label = clean.label
                }
            },

            /**
             * Called by SIDEBAR_BODY_MIXIN.save() for BOTH "Save" and "Save & Close".
             * A non-empty return shows the message in a .v-form-alert toast and the
             * save is abandoned — nothing is emitted to the builder.
             *
             * @returns {string} '' to allow the save, else the reason it is refused.
             */
            preSaveValidation() {
                // Each gate returns '' for controls it does not own, so the first message wins.
                return getOdReferralMappingError(this.control)
                    || getOdCategorizedMappingError(this.control)
                    || getOdUniqueMappingError(this.control, this.formData)
                    || getOdCategorizedOtherProblemsError(this.control, this.formData)
            },

            onUpdateRules(newRules) {
                // ensure control's visibilityRules updated when editor emits
                try {
                    this.$set(this.control, 'visibilityRules', Array.isArray(newRules) ? newRules : [newRules])
                } catch (e) {
                    // ignore
                }
            }
        },
    }
</script>

<style scoped>
.control-upload-category {
    margin: 8px 0 14px;
    padding: 10px 12px;
    background-color: #F8FAFC;
    border: 1px solid #CBD5E1;
    border-radius: 6px;
}

.control-upload-category label {
    display: block;
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 600;
    color: #334155;
}

.upload-category-hint {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.4;
    color: #475569;
}

.control-alert-highlight {
    margin: 8px 0 14px;
    padding: 10px 12px;
    background-color: #FEF2F2;
    border: 1px solid #FCA5A5;
    border-radius: 6px;
}

.control-alert-highlight label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #B91C1C;
    cursor: pointer;
}

.control-alert-highlight input[type="checkbox"] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
}

.alert-highlight-hint {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.4;
    color: #7F1D1D;
}

.alert-highlight-choice-hint {
    display: block;
    margin-top: 8px;
    padding: 8px 10px;
    background-color: #FFFFFF;
    border: 1px dashed #DC2626;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.4;
    color: #7F1D1D;
}

.alert-highlight-choice-hint strong {
    color: #B91C1C;
}
</style>